import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllOrdersOfAdmin } from "../../redux/actions/order";
import { getAllUsers } from "../../redux/actions/user";
import axios from "axios";
import { server } from "../../server";

const AdminAnalytics = () => {
  const dispatch = useDispatch();
  const { adminOrders, adminOrderLoading } = useSelector((state) => state.order);
  const { users } = useSelector((state) => state.user);
  const [timeRange, setTimeRange] = useState("7d");
  const [analyticsData, setAnalyticsData] = useState({
    metrics: null,
    dailyRevenue: [],
    monthlyRevenue: [],
    orderStatusData: [],
    categoryPerformance: [],
    userRegistrations: [],
    productTrends: [],
  });
  const [productCount, setProductCount] = useState(0);

  useEffect(() => {
    dispatch(getAllOrdersOfAdmin());
    dispatch(getAllUsers());
    // Fetch product count
    const fetchProductCount = async () => {
      try {
        const { data } = await axios.get(`${server}/product/admin/all-products`, { withCredentials: true });
        setProductCount(data.products?.length || 0);
      } catch (e) { console.error(e); }
    };
    fetchProductCount();
  }, [dispatch]);

  const calculateAnalytics = useCallback(() => {
    if (!adminOrders || !users) return;

    const now = new Date();
    const getDaysAgo = (days) => new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    let startDate;
    switch (timeRange) {
      case "24h": startDate = getDaysAgo(1); break;
      case "7d": startDate = getDaysAgo(7); break;
      case "30d": startDate = getDaysAgo(30); break;
      case "90d": startDate = getDaysAgo(90); break;
      case "1y": startDate = getDaysAgo(365); break;
      default: startDate = getDaysAgo(7);
    }

    const filteredOrders = adminOrders.filter(
      (order) => new Date(order.createdAt) >= startDate
    );

    // Revenue calculation
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);
    const deliveredOrders = filteredOrders.filter((o) => o.status === "Delivered");
    const deliveredRevenue = deliveredOrders.reduce((sum, o) => sum + (o.totalPrice || 0), 0);
    const pendingOrders = filteredOrders.filter((o) => ["Pending", "Confirmed", "Packed"].includes(o.status));
    const cancelledOrders = filteredOrders.filter((o) => o.status === "Cancelled");

    // Previous period comparison
    const periodLength = (now - startDate) / (1000 * 60 * 60 * 24);
    const prevStart = getDaysAgo(periodLength * 2);
    const prevOrders = adminOrders.filter(
      (o) => new Date(o.createdAt) >= prevStart && new Date(o.createdAt) < startDate
    );
    const prevRevenue = prevOrders.reduce((sum, o) => sum + (o.totalPrice || 0), 0);
    const revenueGrowth = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue) * 100 : 0;

    // AOV
    const avgOrderValue = filteredOrders.length > 0 ? totalRevenue / filteredOrders.length : 0;

    // Daily revenue
    const dailyMap = {};
    filteredOrders.forEach((o) => {
      const day = new Date(o.createdAt).toLocaleDateString("en-IN", { month: "short", day: "numeric" });
      dailyMap[day] = (dailyMap[day] || 0) + (o.totalPrice || 0);
    });
    const dailyRevenue = Object.entries(dailyMap).map(([date, revenue]) => ({ date, revenue: Math.round(revenue) }));

    // Order status distribution
    const statusMap = {};
    filteredOrders.forEach((o) => { statusMap[o.status] = (statusMap[o.status] || 0) + 1; });
    const orderStatusData = Object.entries(statusMap).map(([status, count]) => ({ status, count }));

    // Category performance
    const categoryMap = {};
    filteredOrders.forEach((order) => {
      (order.cart || order.items || []).forEach((item) => {
        const cat = item.category || "Uncategorized";
        if (!categoryMap[cat]) categoryMap[cat] = { orders: 0, revenue: 0 };
        categoryMap[cat].orders += 1;
        categoryMap[cat].revenue += item.discountedPrice || item.price || 0;
      });
    });
    const categoryPerformance = Object.entries(categoryMap)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);

    // User registrations over time
    const regMap = {};
    (users || []).forEach((u) => {
      const d = new Date(u.createdAt);
      if (d >= startDate) {
        const day = d.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
        regMap[day] = (regMap[day] || 0) + 1;
      }
    });
    const userRegistrations = Object.entries(regMap).map(([date, count]) => ({ date, count }));

    // Top products
    const productMap = {};
    filteredOrders.forEach((order) => {
      (order.cart || order.items || []).forEach((item) => {
        const pid = item._id || item.productId;
        if (!productMap[pid]) productMap[pid] = { name: item.name, qty: 0, revenue: 0 };
        productMap[pid].qty += item.qty || 1;
        productMap[pid].revenue += (item.discountedPrice || item.price || 0) * (item.qty || 1);
      });
    });
    const productTrends = Object.values(productMap).sort((a, b) => b.revenue - a.revenue).slice(0, 10);

    setAnalyticsData({
      metrics: {
        totalRevenue: Math.round(totalRevenue),
        deliveredRevenue: Math.round(deliveredRevenue),
        totalOrders: filteredOrders.length,
        deliveredOrders: deliveredOrders.length,
        pendingOrders: pendingOrders.length,
        cancelledOrders: cancelledOrders.length,
        avgOrderValue: Math.round(avgOrderValue),
        revenueGrowth: Math.round(revenueGrowth * 10) / 10,
        totalCustomers: users?.length || 0,
        newCustomers: (users || []).filter((u) => new Date(u.createdAt) >= startDate).length,
        totalProducts: productCount,
      },
      dailyRevenue,
      orderStatusData,
      categoryPerformance,
      userRegistrations,
      productTrends,
    });
  }, [adminOrders, users, timeRange, productCount]);

  useEffect(() => {
    calculateAnalytics();
  }, [calculateAnalytics]);

  const { metrics } = analyticsData;

  if (adminOrderLoading || !metrics) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (val) => {
    if (val >= 100000) return "\u20B9" + (val / 100000).toFixed(1) + "L";
    if (val >= 1000) return "\u20B9" + (val / 1000).toFixed(1) + "K";
    return "\u20B9" + val;
  };

  const statusColors = {
    Pending: { bg: "bg-yellow-100", text: "text-yellow-800" },
    Confirmed: { bg: "bg-blue-100", text: "text-blue-800" },
    Packed: { bg: "bg-indigo-100", text: "text-indigo-800" },
    Shipped: { bg: "bg-purple-100", text: "text-purple-800" },
    "Out for Delivery": { bg: "bg-orange-100", text: "text-orange-800" },
    Delivered: { bg: "bg-green-100", text: "text-green-800" },
    Cancelled: { bg: "bg-red-100", text: "text-red-800" },
    Returned: { bg: "bg-gray-100", text: "text-gray-800" },
    "Refund Processing": { bg: "bg-amber-100", text: "text-amber-800" },
    Refunded: { bg: "bg-teal-100", text: "text-teal-800" },
  };

  const maxDailyRevenue = Math.max(...analyticsData.dailyRevenue.map((d) => d.revenue), 1);
  const maxCategoryRevenue = Math.max(...analyticsData.categoryPerformance.map((c) => c.revenue), 1);

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-sm text-gray-500 mt-1">Track your store performance</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { value: "24h", label: "24h" },
            { value: "7d", label: "7 Days" },
            { value: "30d", label: "30 Days" },
            { value: "90d", label: "90 Days" },
            { value: "1y", label: "1 Year" },
          ].map((range) => (
            <button
              key={range.value}
              onClick={() => setTimeRange(range.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                timeRange === range.value
                  ? "bg-primary-600 text-white shadow-md"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Revenue", value: formatCurrency(metrics.totalRevenue), sub: `${metrics.revenueGrowth > 0 ? "+" : ""}${metrics.revenueGrowth}% vs prev`, color: "from-primary-500 to-primary-700", icon: "\u20B9" },
          { label: "Total Orders", value: metrics.totalOrders, sub: `${metrics.deliveredOrders} delivered`, color: "from-blue-500 to-blue-700", icon: "\uD83D\uDCE6" },
          { label: "Customers", value: metrics.totalCustomers, sub: `${metrics.newCustomers} new`, color: "from-emerald-500 to-emerald-700", icon: "\uD83D\uDC65" },
          { label: "Avg Order Value", value: formatCurrency(metrics.avgOrderValue), sub: `${metrics.totalProducts} products`, color: "from-amber-500 to-amber-700", icon: "\uD83D\uDCB0" },
        ].map((card, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className={`h-1.5 bg-gradient-to-r ${card.color}`}></div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">{card.label}</span>
                <span className="text-lg">{card.icon}</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{card.value}</div>
              <div className="text-xs text-gray-500 mt-1">{card.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
        {analyticsData.dailyRevenue.length > 0 ? (
          <div className="space-y-2">
            {analyticsData.dailyRevenue.map((d, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-16 flex-shrink-0">{d.date}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-primary-500 to-primary-600 h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${Math.max((d.revenue / maxDailyRevenue) * 100, 5)}%` }}
                  >
                    <span className="text-xs font-medium text-white">{formatCurrency(d.revenue)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No revenue data for this period</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Status */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h3>
          <div className="space-y-3">
            {analyticsData.orderStatusData.map((item, i) => {
              const colors = statusColors[item.status] || { bg: "bg-gray-100", text: "text-gray-800" };
              return (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`${colors.bg} ${colors.text} px-2.5 py-1 rounded-full text-xs font-medium`}>
                      {item.status}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                </div>
              );
            })}
            {analyticsData.orderStatusData.length === 0 && (
              <p className="text-gray-400 text-center py-4">No orders in this period</p>
            )}
          </div>
        </div>

        {/* Category Performance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Performance</h3>
          <div className="space-y-3">
            {analyticsData.categoryPerformance.map((cat, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700 font-medium">{cat.name}</span>
                  <span className="text-gray-500">{formatCurrency(cat.revenue)} ({cat.orders} orders)</span>
                </div>
                <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-botanical to-emerald-500 h-full rounded-full"
                    style={{ width: `${(cat.revenue / maxCategoryRevenue) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
            {analyticsData.categoryPerformance.length === 0 && (
              <p className="text-gray-400 text-center py-4">No category data</p>
            )}
          </div>
        </div>
      </div>

      {/* Top Products */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Selling Products</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-2 text-gray-500 font-medium">#</th>
                <th className="text-left py-3 px-2 text-gray-500 font-medium">Product</th>
                <th className="text-right py-3 px-2 text-gray-500 font-medium">Qty Sold</th>
                <th className="text-right py-3 px-2 text-gray-500 font-medium">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {analyticsData.productTrends.map((p, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-3 px-2 text-gray-400">{i + 1}</td>
                  <td className="py-3 px-2 font-medium text-gray-900">{p.name}</td>
                  <td className="py-3 px-2 text-right text-gray-600">{p.qty}</td>
                  <td className="py-3 px-2 text-right font-semibold text-primary-600">{formatCurrency(p.revenue)}</td>
                </tr>
              ))}
              {analyticsData.productTrends.length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-gray-400">No product data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Customers */}
      {analyticsData.userRegistrations.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">New Customer Registrations</h3>
          <div className="flex flex-wrap gap-3">
            {analyticsData.userRegistrations.map((reg, i) => (
              <div key={i} className="bg-primary-50 text-primary-800 px-3 py-2 rounded-lg text-sm">
                <span className="font-semibold">{reg.count}</span>
                <span className="ml-1 text-primary-600">{reg.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-green-700">{metrics.deliveredOrders}</div>
          <div className="text-xs text-green-600 mt-1">Delivered</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-yellow-700">{metrics.pendingOrders}</div>
          <div className="text-xs text-yellow-600 mt-1">Pending</div>
        </div>
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-red-700">{metrics.cancelledOrders}</div>
          <div className="text-xs text-red-600 mt-1">Cancelled</div>
        </div>
        <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-primary-700">{formatCurrency(metrics.deliveredRevenue)}</div>
          <div className="text-xs text-primary-600 mt-1">Confirmed Revenue</div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;